import { useState, type ChangeEvent, type FormEvent } from 'react';
import { Button, Input, Label } from '@/ui/primitives';
import { Crosshair, Search } from 'lucide-react';
import { formatCep, isValidCep, normalizeCep } from '@/domain/cep';
import type { Dictionary } from '@/i18n';

type CepFormProps = {
  dict: Dictionary;
  onSubmit: (cep: string) => void;
  onUseLocation: () => void;
  isBusy: boolean;
};

export const CepForm = ({ dict, onSubmit, onUseLocation, isBusy }: CepFormProps) => {
  const [value, setValue] = useState('');
  const [showError, setShowError] = useState(false);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!isValidCep(value)) {
      setShowError(true);
      return;
    }
    setShowError(false);
    onSubmit(normalizeCep(value));
  };

  return (
    <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-3">
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="cep" className="text-sm">
          {dict.form.cepLabel}
        </Label>
        <Input
          id="cep"
          name="cep"
          type="text"
          inputMode="numeric"
          autoComplete="postal-code"
          enterKeyHint="search"
          placeholder={dict.form.cepPlaceholder}
          value={value}
          hasError={showError}
          /* vammo-ui renders this as a role="alert" paragraph and flips aria-invalid,
             so the error is announced without a second element to wire up. */
          errorMessage={showError ? dict.form.invalidCep : false}
          onChange={(event: ChangeEvent<HTMLInputElement>) => {
            setValue(formatCep(event.target.value));
            setShowError(false);
          }}
          className="h-12 text-lg"
        />
      </div>

      {/* Neon yellow is the Vammo signature accent and the brand doc says to spend
          it sparingly, so it appears once: on the one action this page exists for.
          The colour sits on a span, not on the Button: vammo-ui ships the Tailwind v3
          preflight unlayered, and its `button { color: inherit }` outranks any
          `@layer utilities` text colour. See AGENTS.md. */}
      <Button type="submit" disabled={isBusy} className="h-12 w-full text-base font-bold">
        <span className="flex items-center text-neon-yellow">
          <Search className="mr-2 h-4 w-4" aria-hidden="true" />
          {isBusy ? dict.form.busy : dict.form.submit}
        </span>
      </Button>

      <Button
        type="button"
        variant="ghost"
        disabled={isBusy}
        onClick={onUseLocation}
        className="h-11 w-full text-sm"
      >
        <Crosshair className="mr-2 h-4 w-4" aria-hidden="true" />
        {dict.form.useLocation}
      </Button>
    </form>
  );
};
